/**
 * Normalize checkbox values in form data
 * Converts null/undefined checkbox values to false
 */
export function normalizeCheckboxValues(
    formData: Record<string, any>,
    fields?: Array<{ column_name: string; ui_type: string }>,
): Record<string, any> {
    const normalized = { ...formData };

    // If fields metadata is provided, use it to identify checkboxes
    if (fields) {
        fields.forEach((field) => {
            if (field.ui_type === 'checkbox' && field.column_name in normalized) {
                normalized[field.column_name] = normalized[field.column_name] === true;
            }
        });
    } else {
        // Otherwise, convert any boolean-like values
        Object.keys(normalized).forEach((key) => {
            const value = normalized[key];
            // Only normalize if it looks like a checkbox value (true, false, null, undefined)
            if (value === null || value === undefined || typeof value === 'boolean') {
                normalized[key] = value === true;
            }
        });
    }

    return normalized;
}

/**
 * Transform flat form data into nested address and site_survey structure
 * for Lead and Job forms
 */

const ADDRESS_FIELDS = ['address_line_one', 'address_line_two', 'city', 'county', 'postcode', 'country'];

const SITE_SURVEY_FIELDS = [
    'front_required',
    'front_width',
    'front_height',
    'right_side_required',
    'right_side_width',
    'right_side_height',
    'back_required',
    'back_width',
    'back_height',
    'left_side_required',
    'left_side_width',
    'left_side_height',
    'other_areas_required',
    'other_areas_width',
    'other_areas_height',
    'sides_notes',
    'total_parameter',
    'lifts_required',
    'job_sketch_notes',
    'purpose_of_scaffold',
    'special_requirements',
    'install_obstructions',
    'power_cables_present',
    'power_cables_notes',
    'hazardous_materials_present',
    'hazardous_materials_notes',
    'minimum_access_width',
    'distance_to_parking',
    'distance_from_yard_miles',
    'distance_from_yard_time',
    'travel_access_notes',
    'customer_needs_to_know',
    'job_duration',
    'job_needs_up_by',
    'customer_special_requirements',
    'workforce_access',
    'vehicle_access',
    'lineal_meters_scaffold',
    'anticipated_erect_duration',
    'anticipated_strike_duration',
    'anticipated_erect_men',
    'anticipated_strike_men',
];

export function transformToNestedStructure(formData: Record<string, any>): Record<string, any> {
    const result: Record<string, any> = {};
    const address: Record<string, any> = {};
    const siteSurvey: Record<string, any> = {};

    // Keys that should be handled specially and not passed through directly
    const SPECIAL_KEYS = ['address', 'site_survey', 'siteSurvey', 'address_id', 'site_survey_id'];

    // Separate fields into nested structures
    for (const [key, value] of Object.entries(formData)) {
        if (ADDRESS_FIELDS.includes(key)) {
            address[key] = value;
        } else if (SITE_SURVEY_FIELDS.includes(key)) {
            siteSurvey[key] = value;
        } else if (!SPECIAL_KEYS.includes(key)) {
            // Don't pass through special keys that are handled below
            result[key] = value;
        }
    }

    // Only include address if at least one field has a value OR if updating existing address
    const hasAddressData = Object.values(address).some((v) => v !== null && v !== undefined && v !== '');
    const hasExistingAddress = formData.address_id !== null && formData.address_id !== undefined;

    if (hasAddressData || hasExistingAddress) {
        // Include address_id if it exists in original form data
        if (formData.address_id) {
            address.id = formData.address_id;
        }
        result.address = address;
    }

    // Only include site_survey if at least one field has a value OR if updating existing site_survey
    const hasSiteSurveyData = Object.values(siteSurvey).some((v) => v !== null && v !== undefined && v !== '');
    const hasExistingSiteSurvey = formData.site_survey_id !== null && formData.site_survey_id !== undefined;

    if (hasSiteSurveyData || hasExistingSiteSurvey) {
        // Include site_survey_id if it exists in original form data
        if (formData.site_survey_id) {
            siteSurvey.id = formData.site_survey_id;
        }
        result.site_survey = siteSurvey;
    }

    // Ensure customers contains only UUIDs (not full customer objects)
    if (Array.isArray(result.customers)) {
        result.customers = result.customers
            .map((item: string | { id?: string }) => {
                // If item is an object with an id, extract the id
                if (typeof item === 'object' && item !== null && 'id' in item) {
                    return item.id;
                }
                // If item is already a string, return it
                return typeof item === 'string' ? item : null;
            })
            .filter((id: string | null): id is string => typeof id === 'string' && id.length > 0);
    }

    return result;
}

/**
 * Transform nested address and site_survey structure into flat form data
 * for loading into forms
 */
export function transformToFlatStructure(data: Record<string, any>): Record<string, any> {
    const result: Record<string, any> = { ...data };

    // Flatten address
    if (data.address && typeof data.address === 'object') {
        for (const [key, value] of Object.entries(data.address)) {
            if (key === 'id') {
                result.address_id = value;
            } else {
                result[key] = value;
            }
        }
        delete result.address;
    }

    // Flatten site_survey
    if (data.site_survey && typeof data.site_survey === 'object') {
        for (const [key, value] of Object.entries(data.site_survey)) {
            if (key === 'id') {
                result.site_survey_id = value;
            } else {
                result[key] = value;
            }
        }
        delete result.site_survey;
    }

    // Also flatten siteSurvey (camelCase variant)
    if (data.siteSurvey && typeof data.siteSurvey === 'object') {
        for (const [key, value] of Object.entries(data.siteSurvey)) {
            if (key === 'id') {
                result.site_survey_id = value;
            } else {
                result[key] = value;
            }
        }
        delete result.siteSurvey;
    }

    // Extract customer IDs from customers relationship
    if (Array.isArray(data.customers)) {
        result.customers = data.customers
            .map((customer: string | { id?: string }) => {
                if (typeof customer === 'object' && customer !== null && 'id' in customer) {
                    return customer.id;
                }
                return typeof customer === 'string' ? customer : null;
            })
            .filter((id: string | null): id is string => typeof id === 'string' && id.length > 0);
    }

    return result;
}
